import React, { useState, useEffect } from "react";
import CommonActionMenu from "../../components/common/CommonActionMenu";
import CommonFormActions from "../../components/common/CommonFormActions";
import MainLayout from "../../components/layout/MainLayout";
import PageLoader from "../../components/common/PageLoader";
import CommonTable from "../../components/common/CommonTable";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./Leave.css";

const clampDateYear = (value) => {
  if (!value || typeof value !== "string") {
    return value;
  }

  const isoMatch = value.match(/^(\d+)-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${year.slice(0, 4)}-${month}-${day}`;
  }

  const localeMatch = value.match(/^(\d{2})-(\d{2})-(\d+)$/);
  if (localeMatch) {
    const [, day, month, year] = localeMatch;
    return `${day}-${month}-${year.slice(0, 4)}`;
  }

  return value.slice(0, 10);
};

function LeaveApplications() {
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [currentApplication, setCurrentApplication] = useState(null);
  const [companyOwner, setCompanyOwner] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    leave_type_id: "",
    from_date: "",
    to_date: "",
    subject: "",
    reason: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get("/leave-applications");

      if (response.data.success) {
        setApplications(response.data.data.applications);
        setLeaveTypes(response.data.data.leave_types);
        setCompanyOwner(response.data.data.companyOwner);
      }
    } catch (error) {
      console.error("Failed to fetch leave applications:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load leave applications",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let formattedValue = value;
    if (name === "subject") {
      formattedValue = value.charAt(0).toUpperCase() + value.slice(1);
    } else if (name === "from_date" || name === "to_date") {
      formattedValue = clampDateYear(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.leave_type_id) {
      newErrors.leave_type_id = "Leave type is required";
    }

    if (!formData.from_date) {
      newErrors.from_date = "From date is required";
    }

    if (!formData.to_date) {
      newErrors.to_date = "To date is required";
    }

    if (
      formData.from_date &&
      formData.to_date &&
      formData.from_date > formData.to_date
    ) {
      newErrors.to_date = "To date must be after from date";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.reason.trim()) {
      newErrors.reason = "Reason is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddLeave = () => {
    setFormData({
      leave_type_id: "",
      from_date: "",
      to_date: "",
      subject: "",
      reason: "",
    });
    setErrors({});
    setShowAddModal(true);
  };

  const handleViewLeave = (application) => {
    setCurrentApplication(application);
    setShowViewModal(true);
  };

  const handleEditLeave = (application) => {
    setCurrentApplication(application);
    setFormData({
      leave_type_id: application.leave_type_id,
      from_date: application.from_date,
      to_date: application.to_date,
      subject: application.subject,
      reason: application.reason,
    });
    setErrors({});
    setShowEditModal(true);
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const response = await api.post("/leave-applications", formData);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Leave application submitted successfully",
          timer: 2000,
          showConfirmButton: false,
        });

        setShowAddModal(false);
        fetchData();
      }
    } catch (error) {
      console.error("Failed to submit leave application:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message || "Failed to submit leave application",
      });

      if (error.response?.data?.locked) {
        Swal.fire({
          icon: 'warning',
          title: 'Record Locked',
          text: error.response.data.message,
          confirmButtonColor: '#6557ff',
        });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || 'Failed to update' });
      }
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const response = await api.put(
        `/leave-applications/${currentApplication.id}`,
        formData,
      );

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Leave application updated successfully",
          timer: 2000,
          showConfirmButton: false,
        });

        setShowEditModal(false);
        fetchData();
      }
    } catch (error) {
      console.error("Failed to update leave application:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message || "Failed to update leave application",
      });
    }
  };

  const handleDelete = async (applicationId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this leave application?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6557ff",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await api.delete(`/leave-applications/${applicationId}`);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Leave application deleted successfully",
          timer: 2000,
          showConfirmButton: false,
        });

        fetchData();
      }
    } catch (error) {
      console.error("Failed to delete leave application:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message || "Failed to delete leave application",
      });
    }
  };

  const handleApprove = async (applicationId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to approve this leave application?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, approve it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await api.post(
        `/leave-applications/${applicationId}/approve`,
      );

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Approved!",
          text: "Leave application approved successfully",
          timer: 2000,
          showConfirmButton: false,
        });

        setShowViewModal(false);
        fetchData();
      }
    } catch (error) {
      console.error("Failed to approve leave application:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          "Failed to approve leave application",
      });
    }
  };

  const handleReject = async (applicationId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to reject this leave application?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, reject it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await api.post(
        `/leave-applications/${applicationId}/reject`,
      );

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Rejected!",
          text: "Leave application rejected successfully",
          timer: 2000,
          showConfirmButton: false,
        });

        setShowViewModal(false);
        fetchData();
      }
    } catch (error) {
      console.error("Failed to reject leave application:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message || "Failed to reject leave application",
      });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${date.getDate()}-${months[date.getMonth()]}-${date.getFullYear()}`;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "1":
        return "status-approved";
      case "2":
        return "status-rejected";
      default:
        return "status-pending";
    }
  };

  const isCompanyAdmin = () => {
    return companyOwner;
  };

  const pendingCount = applications.filter(
    (application) => String(application.status) === "0",
  ).length;
  const approvedCount = applications.filter(
    (application) => String(application.status) === "1",
  ).length;
  const rejectedCount = applications.filter(
    (application) => String(application.status) === "2",
  ).length;

  if (loading) {
    return <MainLayout><PageLoader force copy="Loading..." /></MainLayout>;
  }

  const applicationColumns = [
    {
      header: "Sr. No.",
      render: (_, index) => index + 1,
    },
    {
      header: "Leave Type",
      key: "leave_type_name",
    },
    {
      header: "Subject",
      key: "subject",
    },
    {
      header: "Date Between",
      render: (application) => (
        <div className="date-range">
          <span>From: {formatDate(application.from_date)}</span>
          <span>To: {formatDate(application.to_date)}</span>
        </div>
      ),
    },
    {
      header: "Applied By",
      key: "created_by_name",
    },
    {
      header: "Applied Date",
      render: (application) => formatDate(application.created_at),
    },
    {
      header: "Status",
      render: (application) => (
        <span
          className={`status-badge ${getStatusBadgeClass(application.status)}`}
        >
          {application.status_text}
        </span>
      ),
    },
    {
      header: "Action",
      render: (application) => (
        <CommonActionMenu
          ariaLabel={`Actions for application ${application.id}`}
          actions={[
            {
              label: "View",
              icon: "fas fa-eye",
              onClick: () => handleViewLeave(application),
            },
            application.status === 0
              ? {
                label: "Edit",
                icon: "fas fa-edit",
                onClick: () => handleEditLeave(application),
              }
              : null,
            application.status === 0
              ? {
                label: "Delete",
                icon: "fas fa-trash",
                variant: "danger",
                onClick: () => handleDelete(application.id),
              }
              : null,
          ]}
        />
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="leave-applications-container">
        <div className="applications-card">
          <div className="card-header-custom">
            <div className="header-empty">
              <span className="leave-guidance-label">Leave Desk</span>
              <h3>Leave Applications</h3>
              <p className="leave-applications-subtitle">
                Review, submit, and manage employee leave requests in one place.
              </p>
            </div>
            <button className="btn-add-application" onClick={handleAddLeave}>
              + Apply For Leave
            </button>
          </div>

          <div className="leave-guidance-banner">
            <div>
              <span className="leave-guidance-label">Application Snapshot</span>
              <strong>
                {applications.length
                  ? `${applications.length} leave application${applications.length > 1 ? "s" : ""} available`
                  : "No leave applications yet"}
              </strong>
              <p>
                Track pending requests quickly and keep approvals aligned with the latest attendance and leave balance data.
              </p>
            </div>
            <div className="leave-summary-chips">
              <span className="leave-summary-chip">Pending {pendingCount}</span>
              <span className="leave-summary-chip">Approved {approvedCount}</span>
              <span className="leave-summary-chip">Rejected {rejectedCount}</span>
            </div>
          </div>

          <div className="card-body-custom">
            <CommonTable
              columns={applicationColumns}
              data={applications}
              tableClassName="applications-table-clean"
              wrapperClassName="applications-table-wrap"
              noDataClassName="text-center py-5 no-data"
              emptyMessage="No leave applications found"
            />
          </div>
        </div>

        {/* Add Application Modal */}
        {showAddModal && (
          <>
            <div
              className="modal-backdrop-custom"
              onClick={() => setShowAddModal(false)}
            ></div>
            <div className="modal-custom-large">
              <div className="modal-header-custom">
                <h5>Add New Leave Application</h5>
                <button
                  className="close-modal-btn"
                  onClick={() => setShowAddModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form onSubmit={handleSubmitAdd}>
                <div className="modal-body-custom">
                  <div className="form-row-custom">
                    <label>
                      Leave Type<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <select
                        name="leave_type_id"
                        value={formData.leave_type_id}
                        onChange={handleInputChange}
                        className={errors.leave_type_id ? "error" : ""}
                      >
                        <option value="">Select Leave Type</option>
                        {leaveTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                      {errors.leave_type_id && (
                        <span className="error-msg">
                          {errors.leave_type_id}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="form-row-custom">
                    <label>
                      From Date<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="from_date"
                        value={formData.from_date}
                        onChange={handleInputChange}
                        onInput={handleInputChange}
                        max="9999-12-31"
                        className={errors.from_date ? "error" : ""}
                      />
                      {errors.from_date && (
                        <span className="error-msg">{errors.from_date}</span>
                      )}
                    </div>
                  </div>

                  <div className="form-row-custom">
                    <label>
                      To Date<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="to_date"
                        value={formData.to_date}
                        onChange={handleInputChange}
                        onInput={handleInputChange}
                        max="9999-12-31"
                        className={errors.to_date ? "error" : ""}
                      />
                      {errors.to_date && (
                        <span className="error-msg">{errors.to_date}</span>
                      )}
                    </div>
                  </div>

                  <div className="form-row-custom">
                    <label>
                      Subject<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        placeholder="Enter subject"
                        className={errors.subject ? "error" : ""}
                      />
                      {errors.subject && (
                        <span className="error-msg">{errors.subject}</span>
                      )}
                    </div>
                  </div>

                  <div className="form-row-custom">
                    <label>
                      Reason<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <textarea
                        name="reason"
                        value={formData.reason}
                        onChange={handleInputChange}
                        placeholder="Enter reason"
                        rows="5"
                        className={errors.reason ? "error" : ""}
                      ></textarea>
                      {errors.reason && (
                        <span className="error-msg">{errors.reason}</span>
                      )}
                    </div>
                  </div>
                </div>
                <CommonFormActions
                  className="modal-footer-custom"
                  cancelLabel="Close"
                  submitLabel="Submit"
                  onCancel={() => setShowAddModal(false)}
                />
              </form>
            </div>
          </>
        )}

        {/* Edit Application Modal */}
        {showEditModal && (
          <>
            <div
              className="modal-backdrop-custom"
              onClick={() => setShowEditModal(false)}
            ></div>
            <div className="modal-custom-large">
              <div className="modal-header-custom">
                <h5>Edit Leave Application</h5>
                <button
                  className="close-modal-btn"
                  onClick={() => setShowEditModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form onSubmit={handleSubmitEdit}>
                <div className="modal-body-custom">
                  <div className="form-row-custom">
                    <label>
                      Leave Type<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <select
                        name="leave_type_id"
                        value={formData.leave_type_id}
                        onChange={handleInputChange}
                        className={errors.leave_type_id ? "error" : ""}
                      >
                        <option value="">Select Leave Type</option>
                        {leaveTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                      {errors.leave_type_id && (
                        <span className="error-msg">
                          {errors.leave_type_id}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="form-row-custom">
                    <label>
                      From Date<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="from_date"
                        value={formData.from_date}
                        onChange={handleInputChange}
                        onInput={handleInputChange}
                        max="9999-12-31"
                        className={errors.from_date ? "error" : ""}
                      />
                      {errors.from_date && (
                        <span className="error-msg">{errors.from_date}</span>
                      )}
                    </div>
                  </div>

                  <div className="form-row-custom">
                    <label>
                      To Date<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="to_date"
                        value={formData.to_date}
                        onChange={handleInputChange}
                        onInput={handleInputChange}
                        max="9999-12-31"
                        className={errors.to_date ? "error" : ""}
                      />
                      {errors.to_date && (
                        <span className="error-msg">{errors.to_date}</span>
                      )}
                    </div>
                  </div>

                  <div className="form-row-custom">
                    <label>
                      Subject<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className={errors.subject ? "error" : ""}
                      />
                      {errors.subject && (
                        <span className="error-msg">{errors.subject}</span>
                      )}
                    </div>
                  </div>

                  <div className="form-row-custom">
                    <label>
                      Reason<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <textarea
                        name="reason"
                        value={formData.reason}
                        onChange={handleInputChange}
                        rows="5"
                        className={errors.reason ? "error" : ""}
                      ></textarea>
                      {errors.reason && (
                        <span className="error-msg">{errors.reason}</span>
                      )}
                    </div>
                  </div>
                </div>
                <CommonFormActions
                  className="modal-footer-custom"
                  cancelLabel="Close"
                  submitLabel="Submit"
                  onCancel={() => setShowEditModal(false)}
                />
              </form>
            </div>
          </>
        )}

        {/* View Application Modal */}
        {showViewModal && currentApplication && (
          <>
            <div
              className="modal-backdrop-custom"
              onClick={() => setShowViewModal(false)}
            ></div>
            <div className="modal-custom-large">
              <div className="modal-header-custom">
                <h5>Leave Application</h5>
                <button
                  className="close-modal-btn"
                  onClick={() => setShowViewModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body-custom">
                <div className="view-row">
                  <label>Leave Type:</label>
                  <p>{currentApplication.leave_type_name}</p>
                </div>
                <div className="view-row">
                  <label>From Date:</label>
                  <p>{formatDate(currentApplication.from_date)}</p>
                </div>
                <div className="view-row">
                  <label>To Date:</label>
                  <p>{formatDate(currentApplication.to_date)}</p>
                </div>
                <div className="view-row">
                  <label>Subject:</label>
                  <p>{currentApplication.subject}</p>
                </div>
                <div className="view-row">
                  <label>Reason:</label>
                  <p>{currentApplication.reason}</p>
                </div>
                <div className="view-row">
                  <label>Status:</label>
                  <span
                    className={`status-badge ${getStatusBadgeClass(currentApplication.status)}`}
                  >
                    {currentApplication.status_text}
                  </span>
                </div>
              </div>
              <div className="modal-footer-custom">
                {isCompanyAdmin() && (
                  <>
                    <button
                      className="btn-approve"
                      onClick={() => handleApprove(currentApplication.id)}
                    >
                      Approve
                    </button>
                    <button
                      className="btn-reject"
                      onClick={() => handleReject(currentApplication.id)}
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}

export default LeaveApplications;
